/**
 * SleepProtocol — Sleep Descent
 *
 * Session flow:
 *   intro → journal_speak → journal_review → chandra_bhedana
 *         → four_seven_eight → bhramari_sleep → bridge_select → bridge → complete
 *
 * Voice journal:
 *   Web Speech API capture → raw transcript sent to backend
 *   Backend extracts to-do list → user edits table inline → assigns dates
 *   Calendar/Reminders deep-link export (no OAuth)
 *
 * Pranayama:
 *   Chandra Bhedana  90s  — left nostril, slow deep indigo lotus
 *   4-7-8 Breathing  90s  — 19s/cycle × 4 cycles, warm amber lotus
 *   Bhramari         60s  — humming, slow violet tremor
 *
 * Sleep Bridge:
 *   Optional 5-min NSDR variant or skip straight to complete
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { ChevronLeft, Mic, MicOff, Check, Edit2, X, Moon,
         Calendar, Clipboard, Play, Pause, SkipForward } from 'lucide-react';
import { playBell } from '../utils/bell';
import { renderLotus, NADI_LEFT } from '../utils/lotusBreathe';
import { useSpeechCapture } from '../hooks/useSpeechCapture';
import { useMicBreath } from '../hooks/useMicBreath';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Colour palette (evening / descending) ─────────────────────────────────
const INDIGO  = '#5B4FCF';
const AMBER   = '#C9A84C';
const VIOLET  = '#9B59B6';
const DIM     = 'rgba(237,232,223,0.35)';
const TEXT    = '#e8e2d6';
const MUTED   = 'rgba(237,232,223,0.55)';
const SURFACE = '#0f1114';
const SURF2   = '#161a1f';

// ── Step definitions ───────────────────────────────────────────────────────
const STEPS = [
  {
    id: 'journal_speak',
    name: 'Mind Offload',
    sanskrit: 'Manasa Mukti',
    phase: 'OFFLOAD',
    phaseColor: INDIGO,
    description: 'Speak what is on your mind',
    timed: false,
  },
  {
    id: 'journal_review',
    name: 'Review & Plan',
    sanskrit: 'Sankalpa',
    phase: 'OFFLOAD',
    phaseColor: INDIGO,
    description: 'Confirm your to-do list',
    timed: false,
  },
  {
    id: 'chandra_bhedana',
    name: 'Left Nostril Breath',
    sanskrit: 'Chandra Bhedana',
    phase: 'DESCEND',
    phaseColor: INDIGO,
    duration: 90,
    description: 'Left nostril only · 4 in · 4 out',
    breathHint: 'Inhale 4 · exhale 4 · left nostril only',
    canvasColor: INDIGO,
  },
  {
    id: 'four_seven_eight',
    name: '4-7-8 Breath',
    sanskrit: 'Pranava Rechaka',
    phase: 'DESCEND',
    phaseColor: AMBER,
    duration: 90,
    description: 'Inhale 4 · hold 7 · exhale 8',
    breathHint: 'Inhale 4 · hold 7 · exhale 8',
    canvasColor: AMBER,
  },
  {
    id: 'bhramari_sleep',
    name: 'Humming Breath',
    sanskrit: 'Bhramari',
    phase: 'DESCEND',
    phaseColor: VIOLET,
    duration: 60,
    description: 'Inhale · exhale with a low hum',
    breathHint: 'Inhale · exhale with throat hum',
    canvasColor: VIOLET,
  },
];

const BRIDGE_OPTIONS = [
  { label: '5 min', value: 300, desc: 'Standard' },
  { label: '10 min', value: 600, desc: 'Extended' },
  { label: 'Skip', value: 0, desc: 'Protocol only' },
];

// ── Date helpers ───────────────────────────────────────────────────────────
const DATE_OPTIONS = ['Tomorrow', 'This week', 'Next week', 'Someday'];

const toCalendarDate = (option) => {
  const d = new Date();
  if (option === 'Tomorrow')   { d.setDate(d.getDate() + 1); }
  else if (option === 'This week')  { d.setDate(d.getDate() + 3); }
  else if (option === 'Next week')  { d.setDate(d.getDate() + 7); }
  else return null;
  return d;
};

const toGCalDate = (d) => {
  if (!d) return null;
  return d.toISOString().replace(/[-:]/g, '').slice(0, 8);
};

const formatTime = (s) =>
  `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;

// ── Lotus canvas for sleep (evening palette, mic-responsive) ───────────────
const SleepLotus = ({ step, isPlaying, breathPhase, color, amplitude }) => {
  const canvasRef = useRef(null);
  const stateRef  = useRef({ openness: 0.12, rot: 0, lastT: 0, raf: null });
  const ampRef    = useRef(0);
  const propsRef  = useRef({ isPlaying, breathPhase, step });

  // Split amplitude into its own ref for high-frequency updates
  useEffect(() => { ampRef.current = amplitude || 0; }, [amplitude]);
  useEffect(() => { propsRef.current = { isPlaying, breathPhase, step }; });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const st = stateRef.current;
    st.lastT = performance.now();

    const OPEN_MAX  = 0.55;
    const OPEN_REST = 0.11;

    function frame(now) {
      const dt = Math.min((now - st.lastT) / 1000, 0.05);
      st.lastT = now;
      const { isPlaying: ip, breathPhase: bp, step: s } = propsRef.current;
      const amp = ampRef.current;

      let target = OPEN_REST;
      let petalStates = null;
      const micDriven = amp > 0.01;

      if (!ip) {
        st.openness += (OPEN_REST - st.openness) * 0.015;
      } else if (s === 'chandra_bhedana') {
        // Left nostril only — left petals bloom, right stay ghost
        const R_GHOST = 0.07;
        const activeTarget = micDriven
          ? OPEN_REST + amp * (OPEN_MAX - OPEN_REST)
          : (bp === 'inhale' ? OPEN_MAX : OPEN_REST + 0.03);
        const speed = micDriven ? 0.15 : 0.018;
        st.openness += (activeTarget - st.openness) * speed;
        petalStates = [
          st.openness,
          R_GHOST,
          R_GHOST,
          st.openness,
          st.openness,
        ];
      } else if (s === 'bhramari_sleep') {
        // Humming tremor on exhale
        if (micDriven) {
          target = OPEN_REST + amp * (OPEN_MAX - OPEN_REST);
          if (bp !== 'inhale') target += Math.sin(now / 200) * 0.02;
        } else {
          target = bp === 'inhale' ? OPEN_MAX : OPEN_REST + Math.sin(now / 280) * 0.015;
        }
        const speed = micDriven ? 0.15 : 0.018;
        st.openness += (target - st.openness) * speed;
      } else {
        // four_seven_eight
        if (bp === 'hold') {
          if (micDriven) {
            target = st.openness + amp * 0.12;
            st.openness += (target - st.openness) * 0.08;
          }
        } else {
          target = micDriven
            ? OPEN_REST + amp * (OPEN_MAX - OPEN_REST)
            : (bp === 'inhale' ? OPEN_MAX : OPEN_REST);
          const speed = micDriven ? 0.15 : 0.018;
          st.openness += (target - st.openness) * speed;
        }
      }

      st.openness = Math.max(OPEN_REST - 0.03, Math.min(OPEN_MAX, st.openness));

      if (ip) {
        const spd = s === 'chandra_bhedana' ? 0.05 : 0.04;
        const goingIn = bp === 'inhale';
        st.rot += (goingIn ? 1 : -0.2) * dt * spd;
      }

      renderLotus(canvas, st.openness, st.rot, petalStates, color, amp);
      st.raf = requestAnimationFrame(frame);
    }

    st.raf = requestAnimationFrame(frame);
    return () => { if (st.raf) cancelAnimationFrame(st.raf); };
  }, [step, color]);

  return (
    <canvas
      ref={canvasRef}
      width={220} height={220}
      style={{ width: 220, height: 220 }}
    />
  );
};

// ── Breath pill label ──────────────────────────────────────────────────────
const BreathPill = ({ phase, color, stepId }) => {
  const labels = {
    chandra_bhedana:  { inhale: 'inhale · left', exhale: 'exhale · left' },
    four_seven_eight: { inhale: 'inhale 4', hold: 'hold 7', exhale: 'exhale 8' },
    bhramari_sleep:   { inhale: 'inhale', exhale: 'hum ·exhale' },
  };
  const label = labels[stepId]?.[phase] || phase;
  return (
    <div className="flex items-center gap-2 px-3 py-1.5 rounded-full"
      style={{ background: `${color}14`, border: `1px solid ${color}30` }}>
      <div style={{ width: 5, height: 5, borderRadius: '50%', background: color,
        boxShadow: `0 0 5px ${color}90`, flexShrink: 0 }} />
      <span style={{ fontFamily: 'DM Mono, monospace', fontSize: 11,
        letterSpacing: '0.14em', textTransform: 'uppercase', color: `${color}CC` }}>
        {label}
      </span>
    </div>
  );
};

// ── To-do table row ────────────────────────────────────────────────────────
const TodoRow = ({ todo, index, onChange, onRemove }) => {
  const [editingText, setEditingText] = useState(false);
  const [localText, setLocalText]     = useState(todo.text);

  const commitText = () => {
    setEditingText(false);
    if (localText.trim()) onChange({ ...todo, text: localText.trim() });
    else setLocalText(todo.text);
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -4 }}
      className="flex items-start gap-3 py-3"
      style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}
    >
      {/* Index */}
      <span style={{ fontFamily: 'DM Mono, monospace', fontSize: 11,
        color: INDIGO, minWidth: 20, paddingTop: 2 }}>
        {String(index + 1).padStart(2, '0')}
      </span>

      {/* Text */}
      <div className="flex-1 min-w-0">
        {editingText ? (
          <input
            autoFocus
            value={localText}
            onChange={e => setLocalText(e.target.value)}
            onBlur={commitText}
            onKeyDown={e => { if (e.key === 'Enter') commitText(); if (e.key === 'Escape') { setLocalText(todo.text); setEditingText(false); } }}
            className="w-full bg-transparent focus:outline-none text-[13px]"
            style={{ color: TEXT, borderBottom: `1px solid ${INDIGO}60` }}
          />
        ) : (
          <div className="flex items-center gap-2 group">
            <span style={{ fontSize: 13, color: TEXT, lineHeight: 1.5 }}>{todo.text}</span>
            <button onClick={() => setEditingText(true)}
              className="opacity-0 group-hover:opacity-100 transition-opacity">
              <Edit2 style={{ width: 11, height: 11, color: DIM }} />
            </button>
          </div>
        )}
      </div>

      {/* Date selector */}
      <div className="flex-shrink-0 flex gap-1 flex-wrap justify-end" style={{ maxWidth: 200 }}>
        {DATE_OPTIONS.map(opt => (
          <button
            key={opt}
            onClick={() => onChange({ ...todo, by: opt })}
            className="px-2 py-0.5 rounded-sm text-[10px] transition-all"
            style={{
              fontFamily: 'DM Mono, monospace',
              background: todo.by === opt ? `${INDIGO}20` : 'rgba(255,255,255,0.03)',
              border: `1px solid ${todo.by === opt ? `${INDIGO}50` : 'rgba(255,255,255,0.07)'}`,
              color: todo.by === opt ? INDIGO : DIM,
            }}
          >
            {opt}
          </button>
        ))}
      </div>

      {/* Remove */}
      <button onClick={() => onRemove(todo.id)}
        className="flex-shrink-0 p-1 hover:opacity-70 transition-opacity"
        style={{ color: DIM }}>
        <X style={{ width: 12, height: 12 }} />
      </button>
    </motion.div>
  );
};

// ── Export row (calendar / reminders deep links) ───────────────────────────
const ExportRow = ({ todo }) => {
  const d = toCalendarDate(todo.by);
  const gcalUrl = d
    ? `https://calendar.google.com/calendar/r/eventedit?text=${encodeURIComponent(todo.text)}&dates=${toGCalDate(d)}/${toGCalDate(d)}`
    : null;
  const remindersUrl = d
    ? `x-apple-reminderkit://REMCDReminder/?title=${encodeURIComponent(todo.text)}&dueDate=${d.toISOString()}`
    : null;

  if (!todo.by || todo.by === 'Someday') return null;
  return (
    <div className="flex items-center gap-2 mt-1">
      {gcalUrl && (
        <a href={gcalUrl} target="_blank" rel="noreferrer"
          className="text-[10px] px-2 py-0.5 rounded-sm transition-colors"
          style={{ fontFamily: 'DM Mono, monospace', color: '#4A90D9',
            background: 'rgba(74,144,217,0.08)', border: '1px solid rgba(74,144,217,0.2)' }}>
          + Google Cal
        </a>
      )}
      {remindersUrl && (
        <a href={remindersUrl}
          className="text-[10px] px-2 py-0.5 rounded-sm transition-colors"
          style={{ fontFamily: 'DM Mono, monospace', color: DIM,
            background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }}>
          + Reminders
        </a>
      )}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ══════════════════════════════════════════════════════════════════════════
export const SleepProtocol = ({ user }) => {
  const navigate = useNavigate();

  // Session state machine
  const [sessionState, setSessionState] = useState('intro'); // intro | playing | paused | bridge_select | complete
  const [currentStepIdx, setCurrentStepIdx] = useState(0);
  const [stepTimeLeft,   setStepTimeLeft]    = useState(0);
  const [startTime,      setStartTime]       = useState(null);

  // Journal state
  const [journalMode,    setJournalMode]    = useState('idle'); // idle | speaking | processing | reviewing
  const [rawTranscript,  setRawTranscript]  = useState('');
  const [todos,          setTodos]          = useState([]);
  const [extracting,     setExtracting]     = useState(false);
  const [extractError,   setExtractError]   = useState('');
  const [manualInput,    setManualInput]    = useState('');

  // Breath cycle
  const [breathPhase, setBreathPhase] = useState('inhale');
  const breathTimersRef = useRef([]);

  // Misc
  const [isMuted,     setIsMuted]     = useState(false);
  const [saving,      setSaving]      = useState(false);
  const timerRef = useRef(null);

  const currentStep = STEPS[currentStepIdx];
  const isTimed = currentStep?.timed !== false && !!currentStep?.duration;
  const isJournalStep = currentStep?.id === 'journal_speak' || currentStep?.id === 'journal_review';

  // Speech capture
  const speech = useSpeechCapture({
    silenceMs: 3000,
    onSilence: () => {
      if (journalMode === 'speaking') stopSpeaking();
    },
  });

  // Mic breath — active during timed DESCEND steps for amplitude-driven lotus glow
  const mic = useMicBreath();

  // ── Mic breath — start during timed DESCEND steps ───────────────────────
  useEffect(() => {
    if (sessionState === 'playing' && isTimed) {
      mic.start();
    } else {
      mic.stop();
    }
  }, [sessionState, currentStepIdx]);

  // ── Breath sequencer ────────────────────────────────────────────────────
  useEffect(() => {
    breathTimersRef.current.forEach(clearTimeout);
    breathTimersRef.current = [];
    if (sessionState !== 'playing' || !isTimed) return;

    const stepId = currentStep?.id;
    let running = true;

    if (stepId === 'four_seven_eight') {
      // Three-phase cycle: inhale 4s → hold 7s → exhale 8s
      const cycle = () => {
        if (!running) return;
        setBreathPhase('inhale');
        breathTimersRef.current.push(setTimeout(() => {
          if (!running) return;
          setBreathPhase('hold');
          breathTimersRef.current.push(setTimeout(() => {
            if (!running) return;
            setBreathPhase('exhale');
            breathTimersRef.current.push(setTimeout(() => {
              if (running) cycle();
            }, 8000));
          }, 7000));
        }, 4000));
      };
      cycle();
    } else {
      const schedule = {
        chandra_bhedana: [4000, 4000],
        bhramari_sleep:  [3000, 8000],
      };
      const [inMs, exMs] = schedule[stepId] || [5000, 8000];
      const cycle = () => {
        if (!running) return;
        setBreathPhase('inhale');
        breathTimersRef.current.push(setTimeout(() => {
          if (!running) return;
          setBreathPhase('exhale');
          breathTimersRef.current.push(setTimeout(() => { if (running) cycle(); }, exMs));
        }, inMs));
      };
      cycle();
    }

    return () => { running = false; breathTimersRef.current.forEach(clearTimeout); };
  }, [sessionState, currentStepIdx]);

  // ── Step timer ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (sessionState !== 'playing' || !isTimed) return;
    timerRef.current = setInterval(() => {
      setStepTimeLeft(prev => {
        if (prev <= 1) { clearInterval(timerRef.current); advanceStep(); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [sessionState, currentStepIdx]);

  // ── Navigation ──────────────────────────────────────────────────────────
  const advanceStep = useCallback(async () => {
    mic.stop();
    if (!isMuted) await playBell(1);
    const next = currentStepIdx + 1;
    if (next >= STEPS.length) {
      // All pranayama done — offer bridge
      setSessionState('bridge_select');
    } else {
      setCurrentStepIdx(next);
      setStepTimeLeft(STEPS[next].duration || 0);
      setBreathPhase('inhale');
    }
  }, [currentStepIdx, isMuted]);

  const startSession = () => {
    setSessionState('playing');
    setStartTime(Date.now());
    const firstStep = STEPS[0];
    setCurrentStepIdx(0);
    setStepTimeLeft(firstStep.duration || 0);
    if (firstStep.id === 'journal_speak') setJournalMode('idle');
  };

  const bridgeAudioRef = useRef(null);
  const bridgeTimerRef = useRef(null);
  const [bridgeDuration, setBridgeDuration] = useState(0);
  const [bridgeTimeLeft, setBridgeTimeLeft] = useState(0);

  const handleBridgeSelect = async (duration) => {
    if (!isMuted) await playBell(2);
    if (duration === 0) {
      await completeSession([]);
    } else {
      // Start bridge playback
      setBridgeDuration(duration);
      setBridgeTimeLeft(duration);
      setSessionState('bridge');

      const audioFile = duration <= 300 ? '/audio/sleep-bridge-5min.mp3' : '/audio/sleep-bridge-10min.mp3';
      const audio = new Audio(audioFile);
      bridgeAudioRef.current = audio;
      audio.onended = () => completeSession(['sleep_bridge']);
      audio.play().catch(() => {});
    }
  };

  // Bridge countdown timer
  useEffect(() => {
    if (sessionState !== 'bridge') return;
    bridgeTimerRef.current = setInterval(() => {
      setBridgeTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(bridgeTimerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(bridgeTimerRef.current);
  }, [sessionState]);

  // Cleanup bridge audio on unmount
  useEffect(() => {
    return () => {
      if (bridgeAudioRef.current) { bridgeAudioRef.current.pause(); bridgeAudioRef.current = null; }
    };
  }, []);

  // ── Journal helpers ─────────────────────────────────────────────────────
  const startSpeaking = () => {
    setJournalMode('speaking');
    setRawTranscript('');
    speech.reset();
    speech.start();
  };

  const stopSpeaking = useCallback(() => {
    speech.stop();
    const captured = speech.transcript || rawTranscript;
    setRawTranscript(captured);
    setJournalMode('processing');
    if (captured.trim().length > 10) {
      extractTodos(captured);
    } else {
      // Too short — go straight to review with empty list
      setJournalMode('reviewing');
    }
  }, [speech, rawTranscript]);

  const extractTodos = async (transcript) => {
    setExtracting(true);
    setExtractError('');
    try {
      const res = await axios.post(`${API}/sleep/extract-todos`,
        { transcript },
        { withCredentials: true }
      );
      const extracted = (res.data.todos || []).map((t, i) => ({
        id: `todo_${i}_${Date.now()}`,
        text: t.text,
        by: null,
      }));
      setTodos(extracted);
      setJournalMode('reviewing');
    } catch (e) {
      setExtractError('Could not extract to-dos. You can add them manually below.');
      setTodos([]);
      setJournalMode('reviewing');
    } finally {
      setExtracting(false);
    }
  };

  const addManualTodo = () => {
    const text = manualInput.trim();
    if (!text) return;
    setTodos(prev => [...prev, {
      id: `todo_manual_${Date.now()}`,
      text,
      by: null,
    }]);
    setManualInput('');
  };

  const confirmJournal = async () => {
    // Save todos to backend (fire and forget — don't block the user)
    if (todos.length > 0) {
      axios.post(`${API}/sleep/save-todos`,
        { todos: todos.map(t => ({ text: t.text, by: t.by })) },
        { withCredentials: true }
      ).catch(() => {});
    }
    // Move to first timed step
    const timedIdx = STEPS.findIndex(s => s.timed !== false && !!s.duration);
    setCurrentStepIdx(timedIdx);
    setStepTimeLeft(STEPS[timedIdx].duration);
    setBreathPhase('inhale');
    // Brief bell to mark transition
    if (!isMuted) await playBell(1);
  };

  // ── Session complete ────────────────────────────────────────────────────
  const completeSession = async (extraSteps = []) => {
    setSaving(true);
    try {
      const dur = startTime ? Math.round((Date.now() - startTime) / 1000) : 0;
      const stepsCompleted = STEPS.map(s => s.id).concat(extraSteps);
      await axios.post(`${API}/sleep/completions`,
        { duration_seconds: dur, steps_completed: stepsCompleted,
          todo_count: todos.length },
        { withCredentials: true }
      );
    } catch {}
    setSaving(false);
    setSessionState('complete');
  };

  const handleSkip = () => {
    if (isTimed) advanceStep();
  };

  // ── Copy to clipboard ───────────────────────────────────────────────────
  const copyTodos = () => {
    const text = todos.map((t, i) =>
      `${i + 1}. ${t.text}${t.by ? ` — ${t.by}` : ''}`
    ).join('\n');
    navigator.clipboard.writeText(text).catch(() => {});
  };

  // ════════════════════════════════════════════════════════════════════════
  // RENDER STATES
  // ════════════════════════════════════════════════════════════════════════

  // ── INTRO ───────────────────────────────────────────────────────────────
  if (sessionState === 'intro') return (
    <div className="min-h-screen flex flex-col" style={{ background: '#08090b' }}>
      <header className="flex items-center justify-between px-5 pt-5 pb-3">
        <button onClick={() => navigate('/protocol')}
          className="w-8 h-8 rounded-full flex items-center justify-center"
          style={{ background: 'rgba(255,255,255,0.06)' }}>
          <ChevronLeft style={{ width: 14, height: 14, color: DIM }} />
        </button>
        <p style={{ fontFamily: 'DM Mono, monospace', fontSize: 10,
          letterSpacing: '0.2em', textTransform: 'uppercase', color: DIM }}>
          Sleep Descent
        </p>
        <div style={{ width: 32 }} />
      </header>

      <main className="flex-1 flex flex-col items-center justify-center px-7 pb-12">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.23, 1, 0.32, 1] }}
          className="text-center max-w-sm"
        >
          {/* Moon icon */}
          <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-8"
            style={{ background: `${INDIGO}15`, border: `1px solid ${INDIGO}30` }}>
            <Moon style={{ width: 28, height: 28, color: INDIGO }} />
          </div>

          <h1 style={{ fontFamily: 'DM Serif Display, Georgia, serif',
            fontSize: 28, color: TEXT, fontWeight: 400, marginBottom: 12 }}>
            Sleep Descent
          </h1>
          <p style={{ fontSize: 13, color: MUTED, lineHeight: 1.7, marginBottom: 40 }}>
            Clear the day's cognitive load, then descend into deep sleep with three pranayama practices that activate your parasympathetic nervous system.
          </p>

          {/* Phase overview */}
          <div className="flex justify-center gap-8 mb-12">
            {[
              { label: 'OFFLOAD', color: INDIGO, time: '2 min' },
              { label: 'DESCEND', color: VIOLET, time: '4 min' },
              { label: 'BRIDGE', color: '#4CAF7A', time: 'optional' },
            ].map(p => (
              <div key={p.label} className="text-center">
                <div className="w-2 h-2 rounded-full mx-auto mb-2"
                  style={{ background: p.color, boxShadow: `0 0 8px ${p.color}60` }} />
                <p style={{ fontFamily: 'DM Mono, monospace', fontSize: 9,
                  letterSpacing: '0.15em', textTransform: 'uppercase', color: p.color }}>
                  {p.label}
                </p>
                <p style={{ fontSize: 10, color: DIM, marginTop: 2 }}>{p.time}</p>
              </div>
            ))}
          </div>

          {/* Mute toggle */}
          <div className="flex items-center justify-center gap-3 mb-8">
            <button
              onClick={() => setIsMuted(m => !m)}
              className="flex items-center gap-2 px-3 py-1.5 rounded-full transition-all"
              style={{
                background: isMuted ? 'rgba(255,255,255,0.04)' : `${INDIGO}10`,
                border: `1px solid ${isMuted ? 'rgba(255,255,255,0.07)' : `${INDIGO}30`}`,
              }}
            >
              <span style={{ fontFamily: 'DM Mono, monospace', fontSize: 10,
                letterSpacing: '0.12em', textTransform: 'uppercase',
                color: isMuted ? DIM : INDIGO }}>
                {isMuted ? 'Sound off' : 'Sound on'}
              </span>
            </button>
          </div>

          <button
            onClick={startSession}
            className="w-16 h-16 rounded-full flex items-center justify-center mx-auto transition-transform hover:scale-105 active:scale-95"
            style={{ background: INDIGO, boxShadow: `0 0 32px ${INDIGO}35` }}
            data-testid="start-sleep-btn"
          >
            <Play style={{ width: 22, height: 22, color: '#fff', marginLeft: 2 }} fill="white" />
          </button>
          <p style={{ fontSize: 11, color: DIM, marginTop: 12 }}>Tap to begin</p>
        </motion.div>
      </main>
    </div>
  );

  // ── BRIDGE SELECT ────────────────────────────────────────────────────────
  if (sessionState === 'bridge_select') return (
    <div className="min-h-screen flex flex-col items-center justify-center px-7"
      style={{ background: '#08090b' }}>
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center max-w-sm w-full"
      >
        <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-6"
          style={{ background: 'rgba(76,175,122,0.12)' }}>
          <Moon style={{ width: 22, height: 22, color: '#4CAF7A' }} />
        </div>
        <p style={{ fontFamily: 'DM Mono, monospace', fontSize: 9,
          letterSpacing: '0.22em', textTransform: 'uppercase', color: '#4CAF7A', marginBottom: 8 }}>
          DESCEND phase complete
        </p>
        <h2 style={{ fontFamily: 'DM Serif Display, Georgia, serif',
          fontSize: 24, color: TEXT, fontWeight: 400, marginBottom: 8 }}>
          Add a sleep bridge?
        </h2>
        <p style={{ fontSize: 13, color: MUTED, marginBottom: 32, lineHeight: 1.6 }}>
          A voice-guided Yoga Nidra body scan will carry you across the threshold into sleep. Or end here — your nervous system is already primed.
        </p>

        <div className="flex gap-3 w-full">
          {BRIDGE_OPTIONS.map(opt => (
            <button key={opt.value} onClick={() => handleBridgeSelect(opt.value)}
              className="flex-1 py-5 rounded-lg flex flex-col items-center gap-1 transition-all"
              style={{
                background: 'rgba(76,175,122,0.06)',
                border: '1px solid rgba(76,175,122,0.18)',
              }}>
              <Moon style={{ width: 16, height: 16, color: '#4CAF7A', marginBottom: 4 }} />
              <span style={{ fontFamily: 'DM Mono, monospace', fontSize: 15, color: TEXT }}>{opt.label}</span>
              <span style={{ fontFamily: 'DM Mono, monospace', fontSize: 9,
                letterSpacing: '0.1em', textTransform: 'uppercase', color: DIM }}>{opt.desc}</span>
            </button>
          ))}
        </div>
      </motion.div>
    </div>
  );


  // ── BRIDGE PLAYING ─────────────────────────────────────────────────────
  if (sessionState === 'bridge') {
    const mins = Math.floor(bridgeTimeLeft / 60);
    const secs = bridgeTimeLeft % 60;
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-7"
        style={{ background: '#08090b' }}>
        <div style={{ fontFamily: 'DM Mono, monospace', fontSize: 10, letterSpacing: '0.14em',
          color: '#4CAF7A', textTransform: 'uppercase', marginBottom: 24 }}>
          Sleep Bridge
        </div>
        <div style={{ fontFamily: 'DM Mono, monospace', fontSize: 48, fontWeight: 500,
          color: TEXT, marginBottom: 12 }}>
          {mins}:{secs.toString().padStart(2, '0')}
        </div>
        <div style={{ fontFamily: 'DM Serif Display, serif', fontSize: 20, color: TEXT,
          marginBottom: 8 }}>
          Crossing the threshold
        </div>
        <div style={{ fontFamily: 'DM Mono, monospace', fontSize: 10, letterSpacing: '0.1em',
          color: DIM, textTransform: 'uppercase', marginBottom: 40 }}>
          Voice-guided body scan · lie still
        </div>
        <button
          onClick={() => {
            if (bridgeAudioRef.current) { bridgeAudioRef.current.pause(); bridgeAudioRef.current = null; }
            clearInterval(bridgeTimerRef.current);
            completeSession(['sleep_bridge']);
          }}
          className="px-6 py-2 rounded-lg transition-all"
          style={{
            background: 'rgba(255,255,255,0.05)',
            border: '1px solid rgba(255,255,255,0.1)',
            fontFamily: 'DM Mono, monospace',
            fontSize: 11,
            color: MUTED,
            letterSpacing: '0.06em',
          }}
          data-testid="skip-bridge-btn"
        >
          Skip &amp; Complete
        </button>
      </div>
    );
  }

  // ── COMPLETE ─────────────────────────────────────────────────────────────
  if (sessionState === 'complete') return (
    <div className="min-h-screen flex items-center justify-center px-7"
      style={{ background: '#08090b' }}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center max-w-sm w-full"
      >
        <div className="w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center"
          style={{ background: 'rgba(91,79,207,0.12)' }}>
          <Moon style={{ width: 32, height: 32, color: INDIGO }} />
        </div>
        <h1 style={{ fontFamily: 'DM Serif Display, Georgia, serif',
          fontSize: 28, color: TEXT, fontWeight: 400, marginBottom: 12 }}>
          Descend well
        </h1>
        <p style={{ fontSize: 13, color: MUTED, marginBottom: 32, lineHeight: 1.7 }}>
          Your nervous system is primed. The day's tasks are offloaded. Sleep is ready.
        </p>

        {/* Todos summary */}
        {todos.length > 0 && (
          <div className="rounded-lg p-4 mb-6 text-left"
            style={{ background: SURF2, border: '1px solid rgba(255,255,255,0.07)' }}>
            <div className="flex items-center justify-between mb-3">
              <p style={{ fontFamily: 'DM Mono, monospace', fontSize: 10,
                letterSpacing: '0.14em', textTransform: 'uppercase', color: DIM }}>
                {todos.length} item{todos.length !== 1 ? 's' : ''} planned
              </p>
              <button onClick={copyTodos}
                className="flex items-center gap-1 transition-opacity hover:opacity-70"
                style={{ color: DIM }}>
                <Clipboard style={{ width: 11, height: 11 }} />
                <span style={{ fontFamily: 'DM Mono, monospace', fontSize: 10 }}>Copy</span>
              </button>
            </div>
            {todos.slice(0, 3).map((t, i) => (
              <div key={t.id} className="flex items-start gap-2 py-1">
                <span style={{ fontFamily: 'DM Mono, monospace', fontSize: 11, color: INDIGO, minWidth: 18 }}>
                  {String(i + 1).padStart(2, '0')}
                </span>
                <span style={{ fontSize: 12, color: MUTED }}>{t.text}</span>
                {t.by && <span style={{ fontFamily: 'DM Mono, monospace', fontSize: 10,
                  color: DIM, marginLeft: 'auto', flexShrink: 0 }}>{t.by}</span>}
              </div>
            ))}
            {todos.length > 3 && (
              <p style={{ fontFamily: 'DM Mono, monospace', fontSize: 10,
                color: DIM, marginTop: 4 }}>+{todos.length - 3} more</p>
            )}
          </div>
        )}

        <button onClick={() => navigate('/progress')}
          className="w-full py-4 rounded-lg font-medium transition-all"
          style={{ background: INDIGO, color: '#fff', fontSize: 14 }}
          data-testid="sleep-complete-btn">
          {saving ? 'Saving...' : 'Good night'}
        </button>
        <button onClick={() => navigate('/progress')}
          className="w-full text-[12px] py-2 hover:opacity-70 mt-2 transition-opacity"
          style={{ color: DIM }}>
          Return to dashboard
        </button>
      </motion.div>
    </div>
  );

  // ── PLAYING ──────────────────────────────────────────────────────────────
  const phaseColor = currentStep?.phaseColor || INDIGO;

  return (
    <div className="min-h-screen flex flex-col" style={{ background: '#08090b' }}
      data-testid="sleep-protocol-player">

      {/* NAV */}
      <header className="flex items-center justify-between px-5 pt-5 pb-3 h-16">
        <button onClick={() => navigate('/protocol')}
          className="w-8 h-8 rounded-full flex items-center justify-center"
          style={{ background: 'rgba(255,255,255,0.06)' }}>
          <ChevronLeft style={{ width: 14, height: 14, color: DIM }} />
        </button>
        <div className="text-center">
          <p style={{ fontFamily: 'DM Mono, monospace', fontSize: 10,
            letterSpacing: '0.18em', textTransform: 'uppercase', color: phaseColor }}>
            {currentStep?.phase}
          </p>
          <p style={{ fontSize: 11, color: DIM, marginTop: 1 }}>
            {currentStepIdx + 1} / {STEPS.length} · {currentStep?.sanskrit}
          </p>
        </div>
        <button
          onClick={() => setIsMuted(m => !m)}
          className="w-8 h-8 rounded-full flex items-center justify-center"
          style={{ background: 'rgba(255,255,255,0.06)' }}>
          <span style={{ fontFamily: 'DM Mono, monospace', fontSize: 9,
            color: isMuted ? 'rgba(237,232,223,0.25)' : DIM }}>
            {isMuted ? 'off' : 'on'}
          </span>
        </button>
      </header>

      {/* PROGRESS RAIL */}
      <div className="px-7 mb-4">
        <div className="h-[2px] rounded-full overflow-hidden"
          style={{ background: 'rgba(255,255,255,0.06)' }}>
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{
              width: `${((currentStepIdx) / STEPS.length) * 100}%`,
              background: phaseColor,
              opacity: 0.7,
            }}
          />
        </div>
      </div>

      {/* MAIN CONTENT */}
      <main className="flex-1 flex flex-col items-center px-7 pt-2 pb-4">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep?.id}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="w-full flex flex-col items-center"
          >

            {/* ── JOURNAL: SPEAK step ──────────────────────────────────── */}
            {currentStep?.id === 'journal_speak' && (
              <div className="w-full">
                <h2 style={{ fontFamily: 'DM Serif Display, Georgia, serif',
                  fontSize: 22, color: TEXT, fontWeight: 400,
                  textAlign: 'center', marginBottom: 8 }}>
                  What's on your mind?
                </h2>
                <p style={{ fontSize: 12, color: MUTED, textAlign: 'center',
                  marginBottom: 24, lineHeight: 1.6 }}>
                  Close your eyes and speak the tasks, worries, or to-dos circling your mind. Naming them plans them — and your brain can let them go.
                </p>

                {!speech.supported && (
                  <div className="rounded-lg p-4 mb-4"
                    style={{ background: 'rgba(201,168,76,0.08)',
                      border: '1px solid rgba(201,168,76,0.2)' }}>
                    <p style={{ fontSize: 12, color: AMBER }}>
                      Voice capture unavailable in this browser. Type your to-dos directly in the next step.
                    </p>
                  </div>
                )}

                {/* Mic button */}
                {speech.supported && (
                  <div className="flex flex-col items-center gap-4 mb-6">
                    <button
                      onClick={journalMode === 'speaking' ? stopSpeaking : startSpeaking}
                      className="w-20 h-20 rounded-full flex items-center justify-center transition-all"
                      style={{
                        background: journalMode === 'speaking'
                          ? `${INDIGO}25`
                          : `${INDIGO}15`,
                        border: `2px solid ${journalMode === 'speaking' ? INDIGO : `${INDIGO}40`}`,
                        boxShadow: journalMode === 'speaking'
                          ? `0 0 32px ${INDIGO}40`
                          : 'none',
                        animation: journalMode === 'speaking'
                          ? 'sleepMicPulse 1.6s ease-in-out infinite'
                          : 'none',
                      }}
                      data-testid="journal-mic-btn"
                    >
                      {journalMode === 'speaking'
                        ? <MicOff style={{ width: 28, height: 28, color: INDIGO }} />
                        : <Mic style={{ width: 28, height: 28, color: INDIGO }} />
                      }
                    </button>
                    <p style={{ fontFamily: 'DM Mono, monospace', fontSize: 10,
                      letterSpacing: '0.18em', textTransform: 'uppercase',
                      color: journalMode === 'speaking' ? INDIGO : DIM }}>
                      {journalMode === 'speaking' ? 'Tap to stop' : 'Tap to speak'}
                    </p>
                  </div>
                )}

                {/* Live transcript */}
                {(speech.transcript || speech.interimTranscript) && (
                  <div className="rounded-lg p-4 mb-4 min-h-[80px]"
                    style={{ background: SURF2, border: `1px solid ${INDIGO}20` }}>
                    <p style={{ fontSize: 13, color: TEXT, lineHeight: 1.7 }}>
                      {speech.transcript}
                      {speech.interimTranscript && (
                        <span style={{ color: `${INDIGO}80` }}>
                          {' '}{speech.interimTranscript}
                        </span>
                      )}
                    </p>
                  </div>
                )}

                {/* Skip / proceed */}
                <div className="flex gap-3 mt-2">
                  <button
                    onClick={() => { setJournalMode('reviewing'); setCurrentStepIdx(1); }}
                    className="flex-1 py-3 rounded-lg text-[13px] transition-all"
                    style={{ background: 'rgba(255,255,255,0.04)',
                      border: '1px solid rgba(255,255,255,0.07)', color: DIM }}>
                    Skip journal
                  </button>
                  {(journalMode === 'speaking' || speech.transcript) && (
                    <button
                      onClick={journalMode === 'speaking' ? stopSpeaking : () => {
                        setCurrentStepIdx(1);
                        if (speech.transcript) extractTodos(speech.transcript);
                        else setJournalMode('reviewing');
                      }}
                      className="flex-1 py-3 rounded-lg text-[13px] font-medium transition-all"
                      style={{ background: INDIGO, color: '#fff' }}>
                      {journalMode === 'speaking' ? 'Done speaking' : 'Extract to-dos →'}
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* ── JOURNAL: REVIEW step ─────────────────────────────────── */}
            {currentStep?.id === 'journal_review' && (
              <div className="w-full">
                <h2 style={{ fontFamily: 'DM Serif Display, Georgia, serif',
                  fontSize: 22, color: TEXT, fontWeight: 400,
                  textAlign: 'center', marginBottom: 6 }}>
                  Your to-do list
                </h2>
                <p style={{ fontSize: 12, color: MUTED, textAlign: 'center',
                  marginBottom: 20, lineHeight: 1.6 }}>
                  Assign a date to each item. Your brain treats a planned task as resolved — and releases it.
                </p>

                {extracting && (
                  <div className="flex items-center justify-center gap-3 py-8">
                    <div className="w-5 h-5 rounded-full border-2 border-t-transparent animate-spin"
                      style={{ borderColor: INDIGO, borderTopColor: 'transparent' }} />
                    <span style={{ fontFamily: 'DM Mono, monospace', fontSize: 11,
                      color: DIM }}>Extracting to-dos...</span>
                  </div>
                )}

                {extractError && (
                  <div className="rounded-lg p-3 mb-4"
                    style={{ background: 'rgba(201,168,76,0.08)',
                      border: '1px solid rgba(201,168,76,0.2)' }}>
                    <p style={{ fontSize: 12, color: AMBER }}>{extractError}</p>
                  </div>
                )}

                {!extracting && (
                  <>
                    {/* Todo rows */}
                    <div className="rounded-lg overflow-hidden mb-4"
                      style={{ background: SURF2, border: '1px solid rgba(255,255,255,0.07)' }}>
                      {todos.length === 0 ? (
                        <div className="py-6 text-center">
                          <p style={{ fontSize: 12, color: DIM }}>No to-dos captured yet</p>
                        </div>
                      ) : (
                        <div className="px-4">
                          <AnimatePresence>
                            {todos.map((todo, i) => (
                              <TodoRow
                                key={todo.id}
                                todo={todo}
                                index={i}
                                onChange={updated => setTodos(t => t.map(x => x.id === updated.id ? updated : x))}
                                onRemove={id => setTodos(t => t.filter(x => x.id !== id))}
                              />
                            ))}
                          </AnimatePresence>
                        </div>
                      )}
                    </div>

                    {/* Export links per item */}
                    {todos.some(t => t.by && t.by !== 'Someday') && (
                      <div className="rounded-lg p-3 mb-4"
                        style={{ background: 'rgba(255,255,255,0.02)',
                          border: '1px solid rgba(255,255,255,0.05)' }}>
                        <p style={{ fontFamily: 'DM Mono, monospace', fontSize: 9,
                          letterSpacing: '0.14em', textTransform: 'uppercase',
                          color: DIM, marginBottom: 8 }}>
                          Sync to calendar
                        </p>
                        {todos.filter(t => t.by && t.by !== 'Someday').map(t => (
                          <div key={t.id} className="mb-2">
                            <p style={{ fontSize: 12, color: MUTED, marginBottom: 3 }}>{t.text}</p>
                            <ExportRow todo={t} />
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Add manual todo */}
                    <div className="flex gap-2 mb-4">
                      <input
                        value={manualInput}
                        onChange={e => setManualInput(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') addManualTodo(); }}
                        placeholder="Add a to-do manually..."
                        className="flex-1 px-3 py-2 rounded-lg text-[13px] focus:outline-none"
                        style={{ background: SURF2,
                          border: '1px solid rgba(255,255,255,0.08)', color: TEXT }}
                      />
                      <button onClick={addManualTodo}
                        className="px-3 py-2 rounded-lg transition-all"
                        style={{ background: `${INDIGO}20`,
                          border: `1px solid ${INDIGO}40`, color: INDIGO }}>
                        <Check style={{ width: 16, height: 16 }} />
                      </button>
                    </div>

                    <button
                      onClick={confirmJournal}
                      className="w-full py-4 rounded-lg font-medium text-[14px] transition-all"
                      style={{ background: INDIGO, color: '#fff',
                        boxShadow: `0 0 20px ${INDIGO}30` }}
                      data-testid="confirm-journal-btn">
                      Planned — begin breathing
                    </button>
                  </>
                )}
              </div>
            )}

            {/* ── TIMED STEPS: Pranayama ───────────────────────────────── */}
            {isTimed && (
              <>
                {/* Lotus */}
                <div className="mb-4">
                  <BreathPill
                    phase={breathPhase}
                    color={currentStep.canvasColor}
                    stepId={currentStep.id}
                  />
                </div>
                <SleepLotus
                  step={currentStep.id}
                  isPlaying={sessionState === 'playing'}
                  breathPhase={breathPhase}
                  color={currentStep.canvasColor}
                  amplitude={mic.amplitude}
                />

                {/* Timer */}
                <div className="flex flex-col items-center mb-5 mt-2">
                  <span style={{
                    fontFamily: 'DM Mono, monospace', fontSize: 42, fontWeight: 300,
                    color: TEXT, letterSpacing: '-0.02em',
                  }}>
                    {formatTime(stepTimeLeft)}
                  </span>
                  <p style={{ fontFamily: 'DM Mono, monospace', fontSize: 9,
                    letterSpacing: '0.14em', textTransform: 'uppercase',
                    color: `${currentStep.canvasColor}70`, marginTop: 4 }}>
                    {currentStep.breathHint}
                  </p>
                </div>

                <h2 style={{ fontFamily: 'DM Serif Display, Georgia, serif',
                  fontSize: 22, color: TEXT, fontWeight: 400,
                  textAlign: 'center', marginBottom: 4 }}>
                  {currentStep.name}
                </h2>
                <p style={{ fontSize: 12, color: MUTED, textAlign: 'center',
                  fontStyle: 'italic', fontFamily: 'DM Serif Display, Georgia, serif',
                  marginBottom: 2 }}>
                  {currentStep.sanskrit}
                </p>
              </>
            )}

            {/* Step dots */}
            <div className="flex gap-[6px] items-center mt-6">
              {STEPS.map((step, idx) => (
                <div key={step.id}
                  className="transition-all duration-200"
                  style={{
                    width: idx === currentStepIdx ? 18 : 5,
                    height: 5,
                    borderRadius: idx === currentStepIdx ? 3 : '50%',
                    background: idx < currentStepIdx
                      ? `${step.phaseColor}55`
                      : idx === currentStepIdx
                        ? step.phaseColor
                        : 'rgba(255,255,255,0.12)',
                  }} />
              ))}
            </div>
          </motion.div>
        </AnimatePresence>
      </main>

      {/* FOOTER CONTROLS (timed steps only) */}
      {isTimed && (
        <footer className="px-7 py-4 pb-8 flex items-center justify-between"
          style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          <button
            onClick={handleSkip}
            className="text-[12px] hover:opacity-80 transition-opacity"
            style={{ color: DIM }}>
            Skip →
          </button>

          <button
            onClick={() => setSessionState(s => s === 'playing' ? 'paused' : 'playing')}
            className="w-14 h-14 rounded-full flex items-center justify-center transition-transform hover:scale-105 active:scale-95"
            style={{ background: INDIGO, boxShadow: `0 0 28px ${INDIGO}30` }}
            data-testid="sleep-play-pause-btn">
            {sessionState === 'playing'
              ? <Pause style={{ width: 18, height: 18, fill: 'white', color: 'white' }} />
              : <Play  style={{ width: 18, height: 18, marginLeft: 2, fill: 'white', color: 'white' }} />
            }
          </button>

          <div style={{ width: 40 }} />
        </footer>
      )}

      {/* Pulse animation for mic */}
      <style>{`
        @keyframes sleepMicPulse {
          0%,100%{box-shadow:0 0 0 0 ${INDIGO}30}
          50%{box-shadow:0 0 0 16px ${INDIGO}00}
        }
      `}</style>
    </div>
  );
};
